/**
 * PhaseSummaryTimeline — E5 ribbon visualization for phases on the Summary tab.
 *
 * Renders schedule tasks marked with summaryLevel='phase' as time-aligned
 * ribbon bars below the project timeline. Each ribbon has a subtle status-color
 * tint and an inner progress track.
 *
 * Tasks marked summaryLevel='highlight' render as a separate pinned-items list
 * below the ribbons.
 *
 * Design: E5 — Subtle Tint + Inner Track
 * Colors respect the management tab status palette:
 *   blue=complete, green=on track, yellow=warning, red=critical, grey=N/A
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { GanttTask } from '../../api/ganttTasks';
import type { ProjectPhase } from '../../Data/Milestones/milestoneTypes';
import { newGuid } from '../../Data/Milestones/milestoneTypes';
import {
  getPhases, addPhase, updatePhase, deletePhase,
  loadPhases, MILESTONES_CHANGED_EVENT,
} from '../../Data/Milestones/milestoneOverrides';
import styles from './PhaseSummaryTimeline.module.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface PhaseSummaryTimelineProps {
  /** All Gantt tasks for the project (we filter by summaryLevel internally) */
  tasks: GanttTask[];
  /** Project start date (ISO string) */
  projectStart: string | null;
  /** Project end date (ISO string) */
  projectEnd: string | null;
  /** Overall project % complete */
  projectPctComplete?: number;
  /** Project GUID — needed to read standalone phases from store */
  projectGuid?: string;
  /** Whether the current user can add/edit/delete manual phases */
  canEdit?: boolean;
}

type PhaseStatus = 'complete' | 'onTrack' | 'warning' | 'critical' | 'notStarted';

interface PhaseData {
  taskId: number;
  name: string;
  start: Date;
  end: Date;
  pct: number;
  status: PhaseStatus;
  source: 'manual' | 'gantt';
  phaseGuid?: string; // only for manual phases
}

interface HighlightData {
  taskId: number;
  name: string;
  date: Date | null;
  status: PhaseStatus;
  isMilestone: boolean;
}

/* ------------------------------------------------------------------ */
/*  Status derivation                                                  */
/* ------------------------------------------------------------------ */

/** Derive a phase status from % complete, start date, and end date.
 *  Uses a time-elapsed heuristic: if actual % lags behind elapsed time %
 *  by more than WARNING_GAP points, the phase is flagged as warning. */
const WARNING_GAP = 15; // percentage points behind elapsed time to trigger warning

function derivePhaseStatus(pct: number, endDate: Date | null, startDate?: Date | null): PhaseStatus {
  if (pct >= 100) return 'complete';
  if (pct === 0 && endDate && endDate > new Date()) return 'notStarted';
  if (endDate && endDate < new Date() && pct < 100) return 'critical';

  // Time-elapsed heuristic for warning detection
  if (startDate && endDate) {
    const now = Date.now();
    const duration = endDate.getTime() - startDate.getTime();
    if (duration > 0) {
      const elapsed = Math.max(0, Math.min(1, (now - startDate.getTime()) / duration));
      const elapsedPct = elapsed * 100;
      if (elapsedPct - pct >= WARNING_GAP) return 'warning';
    }
  }

  return 'onTrack';
}

const STATUS_COLORS: Record<PhaseStatus, string> = {
  complete: 'var(--info-text)',         // blue
  onTrack: 'var(--color-success)',      // green
  warning: 'var(--color-warning)',      // yellow
  critical: 'var(--color-error)',       // red
  notStarted: 'var(--text-muted)',      // grey
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function parseDate(s: string | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function fmtDateShort(d: Date | null): string {
  if (!d) return '\u2014';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function PhaseSummaryTimeline({
  tasks,
  projectStart,
  projectEnd,
  projectGuid,
  canEdit = false,
}: PhaseSummaryTimelineProps) {
  const { t } = useAppTranslation('pages');
  const { t: tKpi } = useAppTranslation('kpi');
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [todayHovered, setTodayHovered] = useState(false);

  // Re-render when standalone phases change — storeVersion is included in
  // useMemo deps so the phase list recomputes on every store mutation.
  const [storeVersion, setStoreVersion] = useState(0);
  useEffect(() => {
    const handler = () => setStoreVersion((n) => n + 1);
    window.addEventListener(MILESTONES_CHANGED_EVENT, handler);
    return () => window.removeEventListener(MILESTONES_CHANGED_EVENT, handler);
  }, []);

  // Hydrate from API on first render
  useEffect(() => {
    if (projectGuid) loadPhases(projectGuid);
  }, [projectGuid]);

  // ── Crosshair state ──
  const ribbonAreaRef = useRef<HTMLDivElement>(null);
  const [crosshair, setCrosshair] = useState<{
    pct: number;        // percentage position on timeline (0–100)
    dateStr: string;    // formatted date to display in tooltip
    snapped: boolean;   // true when snapped to a phase edge
  } | null>(null);

  // ── Phase CRUD state ──
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formStart, setFormStart] = useState('');
  const [formEnd, setFormEnd] = useState('');
  const [formPct, setFormPct] = useState(0);

  const resetForm = useCallback(() => {
    setAdding(false);
    setEditingId(null);
    setFormName('');
    setFormStart('');
    setFormEnd('');
    setFormPct(0);
  }, []);

  // Count manual phases for sort_order assignment
  const manualPhaseCount = projectGuid ? getPhases(projectGuid).length : 0;

  const handleAddPhase = useCallback(() => {
    if (!projectGuid || !formName.trim()) return;
    const phase: ProjectPhase = {
      phase_guid: newGuid(),
      project_guid: projectGuid,
      name: formName.trim(),
      start_date: formStart || null,
      end_date: formEnd || null,
      percent_complete: Math.max(0, Math.min(100, formPct)),
      sort_order: manualPhaseCount,
      source: 'manual',
      gantt_task_id: null,
    };
    addPhase(projectGuid, phase);
    resetForm();
  }, [projectGuid, formName, formStart, formEnd, formPct, manualPhaseCount, resetForm]);

  const handleEditSave = useCallback(() => {
    if (!projectGuid || !editingId || !formName.trim()) return;
    updatePhase(projectGuid, editingId, {
      name: formName.trim(),
      start_date: formStart || null,
      end_date: formEnd || null,
      percent_complete: Math.max(0, Math.min(100, formPct)),
    });
    resetForm();
  }, [projectGuid, editingId, formName, formStart, formEnd, formPct, resetForm]);

  const handleDeletePhase = useCallback((phaseGuid: string) => {
    if (!projectGuid) return;
    deletePhase(projectGuid, phaseGuid);
  }, [projectGuid]);

  const startEdit = useCallback((p: PhaseData) => {
    setEditingId(p.phaseGuid || null);
    setFormName(p.name);
    setFormStart(p.start ? p.start.toISOString().slice(0, 10) : '');
    setFormEnd(p.end ? p.end.toISOString().slice(0, 10) : '');
    setFormPct(p.pct);
    setAdding(false);
  }, []);

  const phases = useMemo<PhaseData[]>(() => {
    // Gantt-sourced phases
    const ganttPhases: PhaseData[] = tasks
      .filter((tk) => tk.summaryLevel === 'phase' && tk.startDate && tk.endDate)
      .map((tk) => {
        const s = parseDate(tk.startDate)!;
        const e = parseDate(tk.endDate)!;
        return {
          taskId: tk.taskId,
          name: tk.taskName,
          start: s,
          end: e,
          pct: tk.percentComplete,
          status: derivePhaseStatus(tk.percentComplete, e, s),
          source: 'gantt' as const,
        };
      });

    // Manual standalone phases (Standard tier)
    const manualPhases: PhaseData[] = projectGuid
      ? getPhases(projectGuid)
        .filter((p) => p.start_date && p.end_date)
        .map((p, i) => {
          const s = parseDate(p.start_date)!;
          const e = parseDate(p.end_date)!;
          return {
            taskId: -(i + 1), // negative IDs to avoid collision with Gantt taskIds
            name: p.name,
            start: s,
            end: e,
            pct: p.percent_complete,
            status: derivePhaseStatus(p.percent_complete, e, s),
            source: 'manual' as const,
            phaseGuid: p.phase_guid,
          };
        })
      : [];

    return [...ganttPhases, ...manualPhases].sort((a, b) => {
      // Sort by start date, then by taskId
      const dateDiff = a.start.getTime() - b.start.getTime();
      if (dateDiff !== 0) return dateDiff;
      return a.taskId - b.taskId;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- storeVersion forces recompute on manual phase CRUD
  }, [tasks, projectGuid, storeVersion]);

  // Only milestones render here — non-milestone highlighted items
  // are shown in the Schedule Status Completed/Active/Look Ahead sections.
  const highlights = useMemo<HighlightData[]>(() => {
    return tasks
      .filter((tk) => tk.summaryLevel === 'highlight' && tk.isMilestone)
      .map((tk) => ({
        taskId: tk.taskId,
        name: tk.taskName,
        date: parseDate(tk.endDate) || parseDate(tk.startDate),
        status: derivePhaseStatus(tk.percentComplete, parseDate(tk.endDate), parseDate(tk.startDate)),
        isMilestone: true,
      }))
      .sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0));
  }, [tasks]);

  // ── Timeline bounds: use project dates, but widen to encompass all
  // phase/highlight dates so nothing renders outside [0, 100%].
  const rawPStart = parseDate(projectStart);
  const rawPEnd = parseDate(projectEnd);

  const { pStart, pEnd } = useMemo(() => {
    let earliest = rawPStart?.getTime() ?? Infinity;
    let latest = rawPEnd?.getTime() ?? -Infinity;

    for (const p of phases) {
      if (p.start.getTime() < earliest) earliest = p.start.getTime();
      if (p.end.getTime() > latest) latest = p.end.getTime();
    }
    for (const h of highlights) {
      if (h.date) {
        if (h.date.getTime() < earliest) earliest = h.date.getTime();
        if (h.date.getTime() > latest) latest = h.date.getTime();
      }
    }

    // Fallback: if still no valid range, return null
    if (!isFinite(earliest) || !isFinite(latest) || earliest >= latest) {
      return { pStart: rawPStart, pEnd: rawPEnd };
    }
    return { pStart: new Date(earliest), pEnd: new Date(latest) };
  }, [rawPStart, rawPEnd, phases, highlights]);

  const projDuration = pStart && pEnd ? pEnd.getTime() - pStart.getTime() : 1;

  // Clamp positions to [0, 100] to prevent ribbon overflow
  const toLeft = (d: Date) => {
    const raw = ((d.getTime() - (pStart?.getTime() ?? 0)) / projDuration) * 100;
    return Math.max(0, Math.min(100, raw));
  };
  const toWidth = (s: Date, e: Date) => {
    const l = toLeft(s);
    const r = ((e.getTime() - (pStart?.getTime() ?? 0)) / projDuration) * 100;
    return Math.max(0, Math.min(100 - l, r - l));
  };

  // Today line
  const now = new Date();
  const todayPct = pStart && pEnd
    ? Math.min(100, Math.max(0, toLeft(now)))
    : null;

  // Date axis labels — up to 5 evenly spaced
  const dateLabels = useMemo(() => {
    if (!pStart || !pEnd) return [];
    const labels: string[] = [];
    const count = 5;
    for (let i = 0; i < count; i++) {
      const d = new Date(pStart.getTime() + (projDuration * i) / (count - 1));
      labels.push(d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }));
    }
    return labels;
  }, [pStart, pEnd, projDuration]);

  // ── Quarter gridlines ──
  const quarterLines = useMemo(() => {
    if (!pStart || !pEnd) return [];
    const lines: { pct: number; label: string }[] = [];
    const d = new Date(pStart);
    // Advance to next quarter boundary (Q start = Jan, Apr, Jul, Oct)
    const qMonth = Math.ceil((d.getMonth() + 1) / 3) * 3;
    d.setMonth(qMonth, 1);
    d.setHours(0, 0, 0, 0);

    while (d < pEnd) {
      const pct = ((d.getTime() - pStart.getTime()) / projDuration) * 100;
      if (pct > 0 && pct < 100) {
        const qNum = Math.ceil((d.getMonth() + 1) / 3);
        lines.push({
          pct,
          label: `Q${qNum} '${String(d.getFullYear()).slice(2)}`,
        });
      }
      d.setMonth(d.getMonth() + 3);
    }
    return lines;
  }, [pStart, pEnd, projDuration]);

  // ── Crosshair: collect phase edges for snap detection ──
  const phaseEdges = useMemo(() => {
    if (!pStart || !pEnd) return [];
    const edges: { pct: number; date: Date; label: string }[] = [];
    for (const p of phases) {
      const startPct = toLeft(p.start);
      const endPct = toLeft(p.end);
      edges.push({ pct: startPct, date: p.start, label: `${p.name} start` });
      edges.push({ pct: endPct, date: p.end, label: `${p.name} end` });
    }
    return edges;
  }, [phases, pStart, pEnd, projDuration]);

  const SNAP_THRESHOLD_PX = 8;

  const handleRibbonAreaMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const area = ribbonAreaRef.current;
      if (!area || !pStart || !pEnd) return;

      const rect = area.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const rawPct = (x / rect.width) * 100;
      const clampedPct = Math.max(0, Math.min(100, rawPct));

      // Check for snap to nearest phase edge
      let snappedEdge: { pct: number; date: Date } | null = null;
      const pxPerPct = rect.width / 100;
      for (const edge of phaseEdges) {
        const distPx = Math.abs(edge.pct - clampedPct) * pxPerPct;
        if (distPx <= SNAP_THRESHOLD_PX) {
          if (!snappedEdge || distPx < Math.abs(snappedEdge.pct - clampedPct) * pxPerPct) {
            snappedEdge = edge;
          }
        }
      }

      if (snappedEdge) {
        setCrosshair({
          pct: snappedEdge.pct,
          dateStr: snappedEdge.date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
          snapped: true,
        });
      } else {
        // Interpolate date from percentage
        const ms = pStart.getTime() + (clampedPct / 100) * projDuration;
        const d = new Date(ms);
        setCrosshair({
          pct: clampedPct,
          dateStr: d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
          snapped: false,
        });
      }
    },
    [pStart, pEnd, projDuration, phaseEdges],
  );

  const handleRibbonAreaMouseLeave = useCallback(() => {
    setCrosshair(null);
  }, []);

  // ── All hooks called above — safe to bail out now ──
  const showContainer = phases.length > 0 || (canEdit && !!projectGuid);
  if (!showContainer && highlights.length === 0) return null;

  const showEmpty = phases.length === 0 && !adding;

  return (
    <div className={styles.wrapper}>
      {/* Phase ribbons (or empty state with add capability) */}
      {showContainer && (
        <div className={styles.ribbonContainer}>
          {/* Header with optional + button */}
          <div className={styles.phaseHeader}>
            <div className={styles.sectionLabel}>{t('gantt.summaryLevelPhase')}s</div>
            {canEdit && projectGuid && !adding && !editingId && (
              <button
                type="button"
                className={styles.phaseAddBtn}
                onClick={() => { setAdding(true); setEditingId(null); setFormName(''); setFormStart(''); setFormEnd(''); setFormPct(0); }}
                aria-label={tKpi('phases.add')}
              >
                +
              </button>
            )}
          </div>

          {/* Inline add form */}
          {adding && (
            <div className={styles.phaseForm}>
              <div className={styles.phaseFormFields}>
                <input
                  type="text"
                  placeholder={tKpi('phases.name')}
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className={styles.phaseFormInput}
                  autoFocus
                />
                <input
                  type="date"
                  value={formStart}
                  onChange={(e) => setFormStart(e.target.value)}
                  className={styles.phaseFormInput}
                  title={tKpi('phases.startDate')}
                />
                <input
                  type="date"
                  value={formEnd}
                  onChange={(e) => setFormEnd(e.target.value)}
                  className={styles.phaseFormInput}
                  title={tKpi('phases.endDate')}
                />
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={formPct}
                  onChange={(e) => setFormPct(Number(e.target.value) || 0)}
                  className={styles.phaseFormInput}
                  title={tKpi('phases.percentComplete')}
                />
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>%</span>
              </div>
              <div className={styles.phaseFormActions}>
                <button type="button" className={styles.phaseFormCancel} onClick={resetForm}>
                  {tKpi('phases.cancel')}
                </button>
                <button
                  type="button"
                  className={styles.phaseFormSave}
                  onClick={handleAddPhase}
                  disabled={!formName.trim() || !formStart || !formEnd}
                >
                  {tKpi('phases.save')}
                </button>
              </div>
            </div>
          )}

          {/* Empty state */}
          {showEmpty && (
            <div className={styles.phaseEmpty}>{tKpi('phases.empty')}</div>
          )}

          {/* Ribbon timeline area */}
          {phases.length > 0 && (
            <>
              <div
                ref={ribbonAreaRef}
                className={styles.ribbonArea}
                onMouseMove={handleRibbonAreaMouseMove}
                onMouseLeave={handleRibbonAreaMouseLeave}
              >
                {/* Quarter gridlines */}
                {quarterLines.map((gl, i) => (
                  <div
                    key={`q-${i}`}
                    className={styles.gridline}
                    style={{ insetInlineStart: `${gl.pct}%` }}
                  />
                ))}
                {/* Crosshair line + date tooltip */}
                {crosshair && (
                  <div
                    className={`${styles.crosshairLine} ${crosshair.snapped ? styles.crosshairSnapped : ''}`}
                    style={{ insetInlineStart: `${crosshair.pct}%` }}
                  >
                    <span className={styles.crosshairTooltip}>{crosshair.dateStr}</span>
                  </div>
                )}
                {/* Today line — label shown on hover only */}
                {todayPct != null && todayPct > 0 && todayPct < 100 && (
                  <div
                    className={styles.todayLine}
                    style={{ insetInlineStart: `${todayPct}%` }}
                    onMouseEnter={() => setTodayHovered(true)}
                    onMouseLeave={() => setTodayHovered(false)}
                  >
                    {/* Invisible wider hit area for easier hover */}
                    <div className={styles.todayHitArea} />
                    <span className={`${styles.todayLabel} ${todayHovered ? styles.todayLabelVisible : ''}`}>
                      {t('gantt.today')}
                    </span>
                  </div>
                )}
                {phases.map((p, i) => {
                  const left = toLeft(p.start);
                  const width = toWidth(p.start, p.end);
                  const rightEdge = left + width;
                  const isHovered = hoveredIdx === i;
                  const sc = STATUS_COLORS[p.status];
                  const isManual = p.source === 'manual';
                  const isEditing = editingId === p.phaseGuid && isManual;
                  // When the phase extends into the last 30% of the timeline,
                  // constrain the label inside the ribbon to prevent overflow.
                  const nearEnd = rightEdge > 70;

                  // Show inline edit form instead of ribbon
                  if (isEditing) {
                    return (
                      <div key={p.taskId} className={styles.phaseForm} style={{ marginBlockEnd: 4 }}>
                        <div className={styles.phaseFormFields}>
                          <input
                            type="text"
                            value={formName}
                            onChange={(e) => setFormName(e.target.value)}
                            className={styles.phaseFormInput}
                            autoFocus
                          />
                          <input
                            type="date"
                            value={formStart}
                            onChange={(e) => setFormStart(e.target.value)}
                            className={styles.phaseFormInput}
                            title={tKpi('phases.startDate')}
                          />
                          <input
                            type="date"
                            value={formEnd}
                            onChange={(e) => setFormEnd(e.target.value)}
                            className={styles.phaseFormInput}
                            title={tKpi('phases.endDate')}
                          />
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={formPct}
                            onChange={(e) => setFormPct(Number(e.target.value) || 0)}
                            className={styles.phaseFormInput}
                            title={tKpi('phases.percentComplete')}
                          />
                          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>%</span>
                        </div>
                        <div className={styles.phaseFormActions}>
                          <button type="button" className={styles.phaseFormCancel} onClick={resetForm}>
                            {tKpi('phases.cancel')}
                          </button>
                          <button
                            type="button"
                            className={styles.phaseFormSave}
                            onClick={handleEditSave}
                            disabled={!formName.trim() || !formStart || !formEnd}
                          >
                            {tKpi('phases.save')}
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={p.taskId}
                      className={`${styles.ribbonRow} ${isHovered ? styles.ribbonRowHover : ''}`}
                      onMouseEnter={() => setHoveredIdx(i)}
                      onMouseLeave={() => setHoveredIdx(null)}
                    >
                      {/* Ribbon bar — subtle status tint */}
                      <div
                        className={styles.ribbon}
                        style={{
                          insetInlineStart: `${left}%`,
                          width: `${width}%`,
                          background: `color-mix(in srgb, ${sc} ${isHovered ? '18%' : '14%'}, transparent)`,
                          borderColor: `color-mix(in srgb, ${sc} ${isHovered ? '30%' : '20%'}, transparent)`,
                        }}
                      />
                      {/* Content layer — name + progress track.
                           When the phase is near the right edge (>70%), anchor
                           from the right and let the label overflow leftward
                           into the empty staircase space. */}
                      <div
                        className={`${styles.ribbonContent} ${isManual && canEdit ? styles.phaseActionsTarget : ''}`}
                        style={nearEnd ? {
                          insetInlineEnd: `${100 - left - width}%`,
                          insetInlineStart: 'auto',
                          width: 'auto',
                          alignItems: 'flex-end',
                        } : {
                          insetInlineStart: `${left}%`,
                          width: `${width}%`,
                        }}
                      >
                        <div className={styles.ribbonHeader}>
                          <span className={styles.ribbonName}>{p.name}</span>
                          <span className={styles.ribbonPct} style={{ color: sc }}>
                            {p.pct}%
                          </span>
                        </div>
                        {/* Inner progress track */}
                        <div className={styles.progressTrack}>
                          <div
                            className={styles.progressFill}
                            style={{ width: `${p.pct}%`, background: sc }}
                          />
                        </div>
                      </div>
                      {/* Hover-reveal action buttons for manual phases */}
                      {canEdit && isManual && p.phaseGuid && (
                        <span className={styles.phaseActions}>
                          <button
                            type="button"
                            className={styles.phaseActionBtn}
                            onClick={() => startEdit(p)}
                            title={tKpi('phases.edit')}
                            style={{ color: 'var(--text-tertiary)' }}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className={styles.phaseActionBtn}
                            onClick={() => handleDeletePhase(p.phaseGuid!)}
                            title={tKpi('phases.delete')}
                            style={{ color: 'var(--color-error)' }}
                          >
                            ✕
                          </button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Date axis — grounded darker strip */}
              {dateLabels.length > 0 && (
                <div className={styles.dateAxisStrip}>
                  {dateLabels.map((lbl, i) => (
                    <span key={i} className={styles.dateTick}>{lbl}</span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Milestones removed — now handled by MilestoneList in PerformanceSummary. */}
    </div>
  );
}
